import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface ContactDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  contactData: any;
  onSuccess?: () => void;
}

export const ContactDetailsDialog = ({
  open,
  onOpenChange,
  contactId,
  contactData,
  onSuccess,
}: ContactDetailsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [leadQuality, setLeadQuality] = useState(contactData.lead_quality || "");
  const [callType, setCallType] = useState(contactData.call_type || "");
  const [estimatedValue, setEstimatedValue] = useState(contactData.estimated_value || "");
  const [followUpDate, setFollowUpDate] = useState(
    contactData.follow_up_date ? new Date(contactData.follow_up_date).toISOString().slice(0, 16) : ""
  );
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const updates: any = {
      last_contact_date: new Date().toISOString(),
    };

    if (leadQuality) updates.lead_quality = leadQuality;
    if (callType) updates.call_type = callType;
    if (estimatedValue) updates.estimated_value = parseFloat(estimatedValue);
    if (followUpDate) updates.follow_up_date = new Date(followUpDate).toISOString();

    const { error } = await supabase
      .from("contact_submissions")
      .update(updates)
      .eq("id", contactId);

    setLoading(false);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update contact details",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Contact details updated successfully",
      });
      onSuccess?.();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Contact Details</DialogTitle>
          <DialogDescription>
            Update lead quality, type, and scheduling information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lead-quality">Lead Quality</Label>
            <Select value={leadQuality} onValueChange={setLeadQuality}>
              <SelectTrigger>
                <SelectValue placeholder="Select quality..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hot">Hot</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="cold">Cold</SelectItem>
                <SelectItem value="unqualified">Unqualified</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="call-type">Contact Type</Label>
            <Select value={callType} onValueChange={setCallType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inquiry">Inquiry</SelectItem>
                <SelectItem value="quote_request">Quote Request</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
                <SelectItem value="complaint">Complaint</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="estimated-value">Estimated Value ($)</Label>
            <Input
              id="estimated-value"
              type="number"
              value={estimatedValue}
              onChange={(e) => setEstimatedValue(e.target.value)}
              placeholder="0.00"
              step="0.01"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="follow-up">Follow-up Date</Label>
            <Input
              id="follow-up"
              type="datetime-local"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Details
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
